import React from 'react';
import { createContext, useContext, useState } from 'react';
import { SignInType } from '../types/user';
import { AuthObject } from '../types/auth';
import LocalStorageService from '../services/localstorage.service';
import UserController from '../controllers/user.controller';

interface AuthContextType {
    verified: boolean;
    logout: () => void;
    singIn: (data: SignInType) => Promise<string>;
    refreshToken: () => Promise<string>;
    setVerified: (value: boolean) => void;
}

interface Props {
    children: React.ReactElement;
}

const AuthContext = createContext<AuthContextType>({
    verified: false,
    logout: () => {},
    singIn: () => Promise.resolve(''),
    refreshToken: () => Promise.resolve(''),
    setVerified: () => {}
});

export const AUTH_KEY = 'auth.care11.weather';

export const AuthProvider = ({ children }: Props) => {
    const authData = LocalStorageService.get<AuthObject>(AUTH_KEY);
    const currentTime = new Date().getTime();

    const [verified, setVerified] = useState<boolean>(
        !authData ? false : authData.expiresIn > currentTime
    );

    const refreshToken = async (): Promise<string> => {
        if (!authData) return 'No se pudo refrescar el token';

        const response = await UserController.refreshToken();
        if (!response.success) {
            setVerified(false);
            LocalStorageService.remove(AUTH_KEY);
            return response.errorMessage ?? 'Ocurrió un error al refrescar el token';
        }

        const result = response.result as unknown as AuthObject;

        if (result.accessToken === undefined) {
            return 'El token de acceso no es válido';
        }

        const currentDate = new Date().getTime();
        const newTime = currentDate + result.expiresIn! * 1000;
        const newDate = new Date(newTime).getTime();

        LocalStorageService.set<AuthObject>(AUTH_KEY, {
            accessToken: result.accessToken,
            expiresIn: newDate
        });
        setVerified(true);
        return '';
    };

    const singIn = async (data: SignInType): Promise<string> => {
        const areEmpty = data.email.length === 0 || data.password.length === 0;
        const areStrings = typeof data.email === 'string' && typeof data.password === 'string';
        if (areEmpty || !areStrings) return 'Por favor ingrese un correo y contraseña';

        const response = await UserController.signIn(data);

        if (!response.success) {
            setVerified(false);
            LocalStorageService.remove(AUTH_KEY);
            return response.errorMessage ?? 'Ocurrió un error al ingresar';
        }

        const result = response.result as unknown as AuthObject;

        const currentDate = new Date().getTime();
        const newTime = currentDate + result.expiresIn * 1000;
        const newDate = new Date(newTime).getTime();

        LocalStorageService.set<AuthObject>(AUTH_KEY, {
            accessToken: result.accessToken,
            expiresIn: newDate
        });
        setVerified(true);
        return '';
    };

    const logout = () => {
        LocalStorageService.remove(AUTH_KEY);
        setVerified(false);
    };

    const value: AuthContextType = {
        verified,
        logout,
        singIn,
        refreshToken,
        setVerified
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    return useContext(AuthContext);
};
